/**
 * Save Manager - Web version
 *
 * Dual-write: localStorage (sync-safe) + Supabase cloud (async).
 * Auth handled by AuthContext — provides user ID to robustSaveManager.
 */

import robustSaveManager from './robustSaveManager.js';
import { supabase } from './supabase.ts';
import { debug } from './debug.js';

/**
 * Check if save backend is available
 * Always true — localStorage is always available, cloud is best-effort.
 */
export function isSaveAPIAvailable() {
  return true;
}

/**
 * Web version has no Electron
 */
export function isElectron() {
  return false;
}

/**
 * Save path not applicable for web
 */
export async function getSavePath() {
  return null;
}

/**
 * Save game state
 * @param {Object} worldState - The complete game state to save
 * @returns {Promise<{success: boolean, path?: string, error?: string}>}
 */
export async function saveGame(worldState) {
  debug('[SaveManager] saveGame called');
  return await robustSaveManager.save(worldState);
}

/**
 * Load game state
 * @returns {Promise<{success: boolean, data?: Object, metadata?: Object, exists: boolean, error?: string}>}
 */
export async function loadGame() {
  debug('[SaveManager] loadGame called');
  return await robustSaveManager.load();
}

/**
 * Check if a save exists
 * @returns {Promise<boolean>}
 */
export async function saveExists() {
  return await robustSaveManager.exists();
}

/**
 * Delete save (for "New Game")
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteSave() {
  try {
    await robustSaveManager.clear();

    localStorage.removeItem('playerAirline');
    localStorage.removeItem('gameMode');
    localStorage.removeItem('modeSelected');
    localStorage.removeItem('airlineOpsEOS_emergency');
    localStorage.removeItem('isNewGame');

    debug('[SaveManager] All saves and backups cleared');
    return { success: true };
  } catch (error) {
    debug('[SaveManager] Delete failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get user info from Supabase auth session.
 * Returns steamId-shaped object for compatibility with existing code.
 */
export async function getSteamUser() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      return {
        steamId: user.id,
        displayName: user.email ?? 'Anonymous Pilot',
      };
    }
  } catch (err) {
    debug('[SaveManager] Failed to get Supabase user:', err);
  }
  return { steamId: null, displayName: null };
}

/**
 * Debounced save function for use in effects
 * @param {number} delay - Debounce delay in ms (default 2000)
 * @returns {Function} Debounced save function
 */
export function createDebouncedSave(delay = 2000) {
  let timeoutId = null;
  let lastSaveTime = 0;

  return async function debouncedSave(worldState) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(async () => {
      const now = Date.now();
      if (now - lastSaveTime < 1000) {
        return;
      }
      lastSaveTime = now;
      await saveGame(worldState);
    }, delay);
  };
}

export default {
  isSaveAPIAvailable,
  isElectron,
  getSavePath,
  saveGame,
  loadGame,
  saveExists,
  deleteSave,
  getSteamUser,
  createDebouncedSave,
};
